/**
 * Admin V3 — useAdminQuery hook (defined, not yet consumed)
 *
 * Standardised GET hook for every module in Phase 2+. Phase 1 does NOT
 * consume this hook — placeholders make zero fetches. The hook is
 * defined now so the foundation is locked before module work begins.
 *
 * Lessons absorbed:
 *   - AuthContext is memoized (M-HealthCheck cascade): getAuthHeaders()
 *     returns stable references; safe to use in deps without infinite loops.
 *   - AbortController on unmount + params change (M2A pattern) prevents
 *     stale-response races.
 *   - Manual refetch via incrementing refresh key (M2A pattern).
 *
 * API:
 *   const { data, loading, error, refetch } = useAdminQuery(endpoint, params, options?);
 *
 *   endpoint: string path beginning with '/' (e.g. '/admin/bookings')
 *   params:   query params object (optional)
 *   options:  { enabled?: boolean, refetchInterval?: number }
 *     - enabled=false: no fetch fires
 *     - refetchInterval (Phase 2.5+, ms): when > 0, hook polls the endpoint at
 *       this cadence. Default 0 = single-shot fetch (Phase 2.1-2.4 behavior).
 *       Cleared on unmount and on deps change. Backward-compatible: callers
 *       that do not pass refetchInterval see identical behavior to today.
 *
 * Phase 2.5 polling behavior:
 *   - In polling mode, transient fetch failures preserve last-good `data` so
 *     the polled view does not flash empty between successful refreshes.
 *   - Non-polling callers retain the original blank-on-error contract.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';

const API_URL = import.meta.env.VITE_BACKEND_URL + '/api';

// Phase 2.9 defect remediation — defensive error stringification.
//
// FastAPI returns errors in multiple shapes:
//   - Plain 4xx/5xx with string detail: { detail: "Booking not found" }
//   - 422 validation errors with array detail:
//     { detail: [ { loc: ["query","incident_type"], msg: "...", type: "...", input: "..." }, ... ] }
//   - Some custom handlers return object detail: { detail: { code: "...", ... } }
//
// React error #31 ("Objects are not valid as a React child") will crash any
// component that renders an array/object error directly. This helper guarantees
// the hook ALWAYS exposes `error` as a string, regardless of FastAPI shape.
//
// Backward-compatibility: string-detail consumers see identical behavior; only
// previously-crashing array/object cases now resolve to a readable string.
const normalizeErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 validation error: array of {loc, msg, type, input, ...}
    return detail.map((d) => {
      if (!d || typeof d !== 'object') return String(d);
      const loc = Array.isArray(d.loc) ? d.loc.join('.') : '';
      const msg = d.msg || JSON.stringify(d);
      return loc ? `${loc}: ${msg}` : msg;
    }).join('; ');
  }
  if (detail && typeof detail === 'object') {
    try { return JSON.stringify(detail); } catch { return String(detail); }
  }
  return err?.message || 'Unknown error';
};

const useAdminQuery = (endpoint, params = {}, options = {}) => {
  const { enabled = true, refetchInterval = 0 } = options;
  const { getAuthHeaders } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Serialise params so the effect re-runs when their values change but
  // does not re-run if a new object with identical values is passed.
  const paramsKey = useRef('');
  paramsKey.current = JSON.stringify(params || {});

  useEffect(() => {
    if (!enabled) return undefined;
    let controller;
    let intervalId;
    const run = async () => {
      // Abort any in-flight request from a previous tick before starting a new one.
      if (controller) controller.abort();
      controller = new AbortController();
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API_URL}${endpoint}`, {
          headers: getAuthHeaders(),
          params,
          signal: controller.signal,
        });
        setData(r.data);
      } catch (err) {
        if (axios.isCancel(err) || err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
        // Phase 2.9 defect remediation: normalize error to a string before
        // storing in React state, to prevent React error #31 ("Objects are
        // not valid as a React child") when FastAPI returns 422 validation
        // errors with array-shaped `detail`.
        setError(normalizeErrorMessage(err));
        // Behavioral split (Phase 2.5):
        //   - Non-polling callers (refetchInterval=0): preserve original Phase 2.1-2.4
        //     behavior — blank data on error so empty states render.
        //   - Polling callers (refetchInterval>0): keep last-good data on transient
        //     failures so the polled view does not flash empty between refreshes.
        if (!refetchInterval) setData(null);
      } finally {
        setLoading(false);
      }
    };
    run();
    if (refetchInterval && refetchInterval > 0) {
      intervalId = setInterval(run, refetchInterval);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (controller) controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey.current, enabled, refreshKey, refetchInterval]);

  const refetch = useCallback(() => {
    setError(null);
    setRefreshKey((k) => k + 1);
  }, []);

  return { data, loading, error, refetch };
};

export default useAdminQuery;
